/**
 * The syqel.art product catalogue + commerce constants.
 * Worlds here are the consumer-facing set (the full WORLDS library remains the dev/curation
 * gallery); the Director's journey and the scene picker both draw from this list.
 */
import { combineEntitlement } from './syqelToken'
import type { World } from '@syqel/engine'
import { surface, track } from './analytics'
import { PRICE_USD } from './pricing'
import { worldRenderable } from './gpuCaps'
import { Engine, otaProWorld, otaWorldsForVolume } from '@syqel/ota-client'
import { CURATED_ONLY_MODES, DEFAULT_WORLD_ID, TV_HIDDEN_WORLD_IDS, TV_NEW_WORLD_IDS, VOLUMES, curatedPro, isRetired, volumeOfId, type VolumeDef } from './volumes'
import { favorites, senderFavorites } from './favorites'
import { EXTENDED_FX_IDS, SLOW_FX_IDS, fxExtendedReachable, fxRefused, onFxReachabilityChange } from './fxWorlds'
import { farHeavyHere, heavyHere, measuredFps, onHeavyChange, unprovenHere } from './worldCost'

// ⚠ World lists are FUNCTIONS, not consts: the library is dynamic. OTA world packages register
// during boot (and an OTA engine-core swap replaces the whole WORLDS table), so anything
// computed at module-import time would freeze the pre-OTA library. Compute at call time.
//
// Volume MEMBERSHIP is data — VOLUMES in ./volumes.ts. Everything below derives from it; nothing
// here (or in any picker) may name a volume or a world list of its own.

export { DEFAULT_WORLD_ID, VOLUMES, TV_HIDDEN_WORLD_IDS, type VolumeDef } from './volumes'

// id → world, rebuilt when the library changes (an OTA registration grows WORLDS; an engine-core swap
// replaces the table). Every list below resolves its ids through this, ~1,300 lookups per list — a linear
// find each was a quadratic walk on every grid render, on the weakest boxes we ship to.
let indexOf: { table: World[]; size: number; map: Map<string, World> } | null = null
/** A world by id from the live library, through the shared index (O(1); never a linear WORLDS.find). */
export const worldById = (id: string): World | undefined => {
  const table = Engine.WORLDS
  if (!indexOf || indexOf.table !== table || indexOf.size !== table.length) {
    const map = new Map<string, World>()
    for (const w of table) if (!map.has(w.id)) map.set(w.id, w)
    indexOf = { table, size: table.length, map }
  }
  return indexOf.map.get(id)
}

/** Dedupe by world id, first occurrence (= host catalogue order) wins. Needed since the registry's
 *  world:* modules became self-contained SHADOWS of bundled ids (SDK 0.8): a shadowed world is
 *  found by worldById() AND reported by otaWorldsForVolume() — without this every listed world doubles
 *  in the pickers, the gallery grid (duplicate WebGL tiles), and the Director pool. */
const dedupeById = (worlds: World[]): World[] => {
  const seen = new Set<string>()
  return worlds.filter((w) => (seen.has(w.id) ? false : (seen.add(w.id), true)))
}

export function volumeById(id: string): VolumeDef | undefined {
  return VOLUMES.find((v) => v.id === id)
}

// ── WHAT THIS PLAYER CAN LOAD ──────────────────────────────────────────────────────────────────────
// A world in the library is OFFERED only if this player can actually put it on screen right now:
//  - an EXTENDED fx visual (not bundled — fxWorlds.ts) only while the fx base is reachable: offline,
//    every picker, grid and Director pool leaves it out, so the stage never waits on a fetch that
//    cannot succeed (never a black stage);
//  - a SLOW fx visual (curation quality `slow`: > 1.5× night-flight at 1080p) never on a WEAK device —
//    the TV tiers that already run reduced (setWeakDevice, receiver/weakTier.ts);
//  - nor a world THIS device measured too HEAVY to draw smoothly at the resolution floor (worldCost.ts,
//    renderPolicy.ts) — on every surface: definition and smoothness are never what gives.
// All change at runtime and fire the offer signal (onOfferChange), like entitlement.
let weakDevice = false

/** The TV receiver declares a weak tier (sub-1GB boxes, the webOS reduced pipeline): slow fx visuals
 *  leave every list there, and the lite line joins them (tierOffered). */
export function setWeakDevice(on: boolean): void {
  if (on === weakDevice) return
  weakDevice = on
  notifyOfferChange()
}

// ── THE LITE LINE (volumes.ts `tier: 'lite'`) is offered only where its tier is: on a WEAK TV tier (the flag
// above), and on a SENDER exactly the lite worlds the TV it casts to offers (the offer frame's `extra`) — the
// desktop from its cast target, the phone surface from what its shell sends (the shell lists a lite world only
// while the TV it casts to offers it). One gate, `tierOffered`, used by every list and by tvAuthorized; this is
// the only state it reads. Anywhere else a lite world is claimed but never listed.
let senderLite: ReadonlySet<string> = new Set()

/** The lite worlds a SENDER may offer now — the ones the TV it casts to offers (`extra`); [] when not casting to
 *  one. Each sender surface has one source for it (the desktop's cast target, the phone shell's drive). */
export function setSenderLite(ids: readonly string[]): void {
  const next = new Set(ids)
  if (next.size === senderLite.size && [...next].every((id) => senderLite.has(id))) return
  senderLite = next
  notifyOfferChange()
}

/** Is the lite line offered on this device (a weak TV tier)? */
export function liteOfferedHere(): boolean {
  return weakDevice
}

/** May this world's TIER be offered here? Every world outside a tier volume may; a lite world only on a weak TV, or
 *  on a sender casting to a TV that offers it. */
function tierOffered(w: World): boolean {
  return !isLite(w) || weakDevice || senderLite.has(w.id)
}

/** A lite-line world: one the lite volume names, or one the engine marks lite (World.tier — a newer SDK's, not yet
 *  named here, stays out of every list and Experiments too). */
export function isLite(w: World): boolean {
  return volumeOfId(w.id)?.tier === 'lite' || Engine.isLiteWorld?.(w) === true
}

/** May a LIST here offer this world — every list the user picks from or the Director draws on (a volume, the
 *  favorites): in the library (not retired), its tier offered here (tierOffered), loadable now. One gate, so no list
 *  can offer what another withholds (a starred lite world after the weak TV that offered it is gone). */
function listedHere(w: World, ignoreCost: boolean): boolean {
  return !isRetired(w) && tierOffered(w) && loadable(w.id, ignoreCost)
}

/** Can this player load and run this world right now (bundled, or its host reachable; not too heavy)?
 *  `ignoreCost` asks only whether it CAN run here, not whether it runs well (the cost gates). */
export function loadable(id: string, ignoreCost = false): boolean {
  return !unavailableHere(id, ignoreCost)
}

/** Why this player can't offer a world right now — or null when it can. THE one classification every list
 *  derives from (through `loadable`), and what a TV tells the phone casting to it (receiver/offer.ts):
 *   - 'unreachable': an extended fx visual whose host is offline, or whose package failed its integrity check;
 *   - 'heavy': measured too heavy here (worldCost.ts), or a `slow` fx visual on a weak tier.
 *  `ignoreCost` asks only whether it CAN run here (the reachability gate alone). */
export function unavailableHere(id: string, ignoreCost = false): 'unreachable' | 'heavy' | null {
  if (EXTENDED_FX_IDS.has(id) && (!fxExtendedReachable() || fxRefused(id))) return 'unreachable'
  if (ignoreCost) return null
  return heavyHere(id) || (weakDevice && SLOW_FX_IDS.has(id)) ? 'heavy' : null
}

/**
 * The worlds of one volume that can render RIGHT NOW, in its order: the ids it names that resolve in
 * the library (bundled, or registered by an OTA package) and are loadable here (see `loadable`), then —
 * Volumes 1/2 only — OTA packages that place themselves in it by metadata. An OTA placement never pulls in a world VOLUMES names elsewhere,
 * nor a curated-only (fx) world: ota-client defaults an undeclared `volume` to 1, so without this
 * every pack visual on the registry would land in Volume 1.
 */
export function volumeWorlds(volume: VolumeDef | string, ignoreCost = false): World[] {
  const v = typeof volume === 'string' ? volumeById(volume) : volume
  if (!v) return []
  return memo(`vw|${v.id}|${ignoreCost}`, () => resolveVolume(v, ignoreCost))
}
function resolveVolume(v: VolumeDef, ignoreCost: boolean): World[] {
  const named = v.worlds.map(worldById).filter((w): w is World => Boolean(w))
  const placed = v.otaVolume === undefined
    ? []
    : otaWorldsForVolume(v.otaVolume).filter((w) => !volumeOfId(w.id) && !CURATED_ONLY_MODES.includes(w.mode))
  return dedupeById([...named, ...placed]).filter((w) => listedHere(w, ignoreCost))
}

/**
 * The volumes a user can see right now: listed, and with at least one world that renders. A volume
 * whose worlds haven't arrived (the fx volumes before their packages ship) appears in NO surface.
 */
export function offeredVolumes(): VolumeDef[] {
  return memo('offered', () => VOLUMES.filter((v) => v.listed !== false && volumeWorlds(v).length > 0))
}

// ── THE LISTS ARE COMPUTED ONCE PER STATE, not per call. Every list here is a pure function of: the library
// (Engine.WORLDS — an OTA registration grows it, an engine-core swap replaces it), the offer (entitlement,
// fx reachability, the weak tier, measured cost — every change fires notifyOfferChange, which bumps
// offerRev) and the casting sender's favorites (replaced whole on change). A receiver asks for them from
// its render loop and every grid render; walking ~1,300 worlds each time cost a weak TV 60–100 ms a frame.
let offerRev = 0
let memoKey: { table: World[]; size: number; rev: number; favs: readonly string[] } | null = null
const memoed = new Map<string, unknown>()
function memo<T>(name: string, compute: () => T): T {
  const table = Engine.WORLDS
  const favs = senderFavorites()
  if (!memoKey || memoKey.table !== table || memoKey.size !== table.length || memoKey.rev !== offerRev || memoKey.favs !== favs) {
    memoKey = { table, size: table.length, rev: offerRev, favs }
    memoed.clear()
  }
  if (!memoed.has(name)) memoed.set(name, compute())
  return memoed.get(name) as T
}

/** The offered volume a world currently belongs to (named or OTA-placed). */
export function volumeOfWorld(id: string): VolumeDef | undefined {
  return offeredVolumes().find((v) => volumeWorlds(v).some((w) => w.id === id))
}

/** The first (default) volume's worlds — the fallback world set when nothing else resolves. */
export function artWorlds(): World[] {
  return volumeWorlds(VOLUMES[0])
}

/** The default visual — the opening world and the fallback for an id that doesn't resolve: DEFAULT_WORLD_ID
 *  (volumes.ts), unless THIS device measured it heavy (worldCost.ts) — the first visual can never be one the
 *  device can't show smoothly. Then it is the first playable world, in the default's own volume and then the
 *  journey's order, that this device has judged smooth; failing that, the first not judged heavy (worlds a device has
 *  never shown carry no verdict yet). */
export function defaultWorld(): World {
  const authored = worldById(DEFAULT_WORLD_ID)
  if (authored && !heavyHere(authored.id)) return authored
  return memo('defaultWorld', () => {
    const home = volumeOfId(DEFAULT_WORLD_ID)
    // loadable (none of them heavy here) and playable on this entitlement — the first visual is never locked
    const order = [...(home ? volumeWorlds(home) : []), ...journeyWorlds()].filter((w) => worldAvailable(w.id))
    return order.find((w) => measuredFps(w.id) !== undefined) ?? order[0] ?? authored ?? artWorlds()[0]
  })
}

/** The Director's journey pool — it travels across ALL worlds in the offered volumes, not just V1.
 *  The experiments lane (calibration grid, unchosen variants, …) stays out of the journey. */
export function journeyWorlds(): World[] {
  return offeredVolumes().flatMap((v) => volumeWorlds(v))
}

/**
 * Premium (Pro-only) worlds — every Pro volume minus its free showcase. These are the "added volumes
 * of visuals + premium visuals" the SYQEL Art Pro subscription unlocks: an ongoing,
 * continually-expanding library (not a static one-time unlock). VOLUMES decides for every world it
 * names; an OTA package's signed metadata decides only for worlds no volume names.
 */
export function isProWorld(id: string): boolean {
  return curatedPro(id) ?? otaProWorld(id) ?? false
}

/** Can the current user play this world right now? (free worlds always; premium needs Pro) */
export function worldAvailable(id: string): boolean {
  return !isProWorld(id) || isSubscribed()
}

/**
 * The Director's journey pool for the CURRENT entitlement: Volume 1 + the free showcases always;
 * the full premium volumes only when subscribed. Keeps the free auto-VJ from silently handing out
 * premium visuals, and gives Pro users the fuller journey.
 */
export function journeyPool(): World[] {
  return journeyWorlds().filter((w) => worldAvailable(w.id))
}

/**
 * Worlds in the library that no volume claims — the "experiments" lane. Never in it: worlds a volume
 * names (listed OR held — held = pack imports awaiting curation), retired worlds, and curated-only
 * families (fx), so an SDK bump that adds content to the library does not flood this lane.
 */
export function experimentWorlds(): World[] {
  const claimed = new Set([...VOLUMES.flatMap((v) => v.worlds), ...journeyWorlds().map((w) => w.id)])
  return Engine.WORLDS.filter((w) => !claimed.has(w.id) && !isRetired(w) && !CURATED_ONLY_MODES.includes(w.mode) && !isLite(w))
}

// ── FAVORITES (favorites.ts holds the store; these are the world lists derived from it) ──────────
/** The filter id every picker uses for "★ Favorites", beside the volume ids (never a VOLUMES id). */
export const FAVORITES_FILTER = 'fav'

/** This player's starred worlds that can render right now, newest first. A star on a world that has
 *  left the library (retired, or an OTA package not loaded) — or that this device doesn't offer (a lite world once
 *  no weak TV offers it) — stays recorded but isn't offered.
 *  `ignoreCost` keeps the ones this device measured heavy (a picker lists them, marked — heavyHere). */
export function favoriteWorlds(ignoreCost = false): World[] {
  return favorites.list().map(worldById).filter((w): w is World => !!w && listedHere(w, ignoreCost))
}

/**
 * The Director's pool when the user journeys their favorites: the starred worlds the CURRENT
 * entitlement can play (a starred Pro world still needs Pro — a star is not an unlock). Falls back to
 * the full journeyPool when none are playable, so the Director is never handed an empty pool.
 */
export function favoritesJourneyPool(): World[] {
  const pool = favoriteWorlds().filter((w) => worldAvailable(w.id))
  return pool.length ? pool : journeyPool()
}

/** The gallery view filter: an offered volume's id, 'exp' (Experiments — everything not in a volume),
 *  or FAVORITES_FILTER (this player's starred worlds). A picker LISTS what this device measured too heavy
 *  (it marks them — heavyHere — and a pick still plays); only the Director's pools leave them out. */
export type GalleryFilter = string
export function galleryWorlds(filter: GalleryFilter): World[] {
  if (filter === FAVORITES_FILTER) return favoriteWorlds(true)
  return filter === 'exp' ? experimentWorlds() : volumeWorlds(filter, true)
}

// ── TV parity (Android TV grid / tvOS home) ─────────────────────────────────────────────────────
// The TV home grid: every offered volume in order, minus the two non-reactive MVP shaders (tvOS
// parity). The phone pickers read the same list from the build (dist/catalogue.json) — thumbnails
// live in apps/web/public/previews/<id>.jpg.


/** A TV grid tile. `volume` is its VolumeDef id; `starred` = in the CASTING SENDER's favorites. */
export interface TvWorld { id: string; name: string; volume: string; isNew: boolean; locked: boolean; starred: boolean }

/**
 * The TV home grid: every offered volume in order (minus the hidden shaders) and only what this
 * device can render (gpuCaps — the engine's needs logic: a WebGL1-only panel loses the three.js worlds), each tile tagged with
 * its volume, whether it arrived after this build shipped (OTA) or is one of the flagged-newest
 * worlds, and whether it is Pro-locked for the CURRENT entitlement.
 *
 * Locked worlds stay VISIBLE on the grid — they are the upsell. The TV has no purchase UI of its own
 * (entitlement arrives out-of-band: a subscribed sender announces it over the cast control channel,
 * or the native shell injects `window.__syqelEntitled`), so a locked tile points at the phone/desktop
 * app rather than at a checkout the remote cannot drive.
 *
 * ⚠ Entitlement is ASYNC — call this again on every offer change (see onOfferChange).
 */
export function tvHomeWorlds(): TvWorld[] {
  return memo('tvHome', computeTvHome)
}
function computeTvHome(): TvWorld[] {
  const hidden = new Set(TV_HIDDEN_WORLD_IDS)
  const isNew = (id: string): boolean => TV_NEW_WORLD_IDS.includes(id) || !volumeOfId(id)
  const starred = new Set(senderFavorites())
  return offeredVolumes().flatMap((v) => volumeWorlds(v).filter((w) => !hidden.has(w.id) && worldRenderable(w))
    .map((w) => ({ id: w.id, name: w.name, volume: v.id, isNew: isNew(w.id), locked: !worldAvailable(w.id), starred: starred.has(w.id) })))
}

/**
 * The TV grid's "★ Favorites" filter: the casting sender's starred worlds that this TV's grid offers,
 * in the SENDER's order (newest first). Empty with no sender, or a sender without favorites — the rail
 * hides the filter then. ⚠ Session state: call again on every onSenderFavoritesChange.
 */
export function tvFavoriteWorlds(): TvWorld[] {
  const grid = new Map(tvHomeWorlds().map((w) => [w.id, w]))
  return senderFavorites().map((id) => grid.get(id)).filter((w): w is TvWorld => Boolean(w))
}

/**
 * The Director's journey pool on the TV — the journey minus the hidden shaders (tvOS parity), gated
 * to what the current entitlement can actually play. It is also gated by what THIS device measured it
 * can draw smoothly (worldCost.ts — every list is, through loadable()). That replaced a hand-kept "light
 * worlds" list for sub-1GB boxes, whose six 3D worlds measured 6–8 fps on the 1 GB EcoTV themselves.
 *
 * The gate matters most HERE: the auto-journey is what an unattended TV plays all evening, so an
 * ungated pool hands out the whole premium library to a free user without them ever choosing it.
 * ⚠ Entitlement is ASYNC — call this again on every offer change (onOfferChange). Never rebuild the Director
 * for it (a new Director makes a new opening pick = swaps the visible world): hand the running one
 * the new pool with Director.setPool (receiver/journey.ts).
 */
export function tvJourneyPool(): World[] {
  return memo('tvPool', computeTvJourneyPool)
}
function computeTvJourneyPool(): World[] {
  const hidden = new Set(TV_HIDDEN_WORLD_IDS)
  const playable = (w: World): boolean => !hidden.has(w.id) && worldAvailable(w.id) && worldRenderable(w)
  const pool = journeyWorlds().filter(playable)
  if (pool.length >= 3) return pool
  // a device that measured nearly everything too heavy: never strand the Director — journey on the lightest
  // it knows (unmeasured first: they may yet pass). Never a world UNPROVEN on a weak GPU (devicePriors.ts): that
  // is exactly the slideshow the basis exists to keep off an unattended screen.
  const fps = (w: World): number => measuredFps(w.id) ?? Infinity
  return offeredVolumes().flatMap((v) => volumeWorlds(v, true)).filter((w) => playable(w) && !unprovenHere(w.id)).sort((a, b) => fps(b) - fps(a)).slice(0, Math.max(3, pool.length))
}

/** May this TV show this world on its OWN authority (a deep link, the viewer's pick) — it exists here, the
 *  current entitlement covers it, this device can draw it, and it isn't hidden from TVs? Authorization is
 *  this predicate, never a world's presence in (or absence from) a list: the lists also leave worlds out for
 *  reasons that are not authorization (a host not yet reachable, a measured cost), and "absent" must never
 *  read as "allowed". Availability is judged separately (tvStillPlayable), as the offer settles. */
export function tvAuthorized(id: string): boolean {
  const w = worldById(id)
  return !!w && worldAvailable(id) && worldRenderable(w) && tierOffered(w) && !TV_HIDDEN_WORLD_IDS.includes(id)
}

/** May this TV SHOW this world — put it on screen, or keep it there: authorized (tvAuthorized), loadable here, and
 *  not CONCLUSIVELY far over the smoothness bar (worldCost.farHeavyHere — p95 ≥ FAR_MISS × the bar: a slideshow).
 *  ONE rule for entry and for keeping: a world merely heavy NEAR the bar leaves the lists for the next pick but is
 *  not taken off screen mid-song for it; a far miss is never held (receiver/selection.leave sends the screen back to
 *  what it showed before). */
export function tvStillPlayable(id: string): boolean {
  return tvMayEnter(id) && loadable(id, true)
}

/** May a TV-local entry (the ?world= deep link) put this world on screen at boot — authorized and not far over the
 *  bar here? Loadability is left to tvStillPlayable as the offer settles (an extended visual's host isn't known to be
 *  reachable yet at boot). The same far rule as keeping it. */
export function tvMayEnter(id: string): boolean {
  return tvAuthorized(id) && !farHeavyHere(id)
}

// Stripe Payment Links (product prod_UfzFtncbPKKfwc — SYQEL Art) on the $10 / $79 prices. Existing
// subscribers stay on the $8 / $60 prices they signed up at; nothing migrates them.
const PAYMENT_LINK_MONTHLY = 'https://pay.syqel.com/b/cNi9AUabU2t56iFdHT2Fa08'
const PAYMENT_LINK_ANNUAL = 'https://pay.syqel.com/b/28EbJ2fwe6JlcH3cDP2Fa07'

/** Stripe checkout per plan; labels derive from PRICE_USD (pricing.ts) so they can't drift from the link. */
export const SUBSCRIBE = {
  monthly: { label: `$${PRICE_USD.monthly} / month`, url: PAYMENT_LINK_MONTHLY },
  annual: { label: `$${PRICE_USD.annual} / year`, url: PAYMENT_LINK_ANNUAL }
}

/** Stripe customer billing portal. */
export const MANAGE_SUBSCRIPTION_URL = 'https://pay.syqel.com/p/login/3cs8zg7mYb3r1AA8ww'

/**
 * Open an external URL in the user's DEFAULT BROWSER. In the desktop (Tauri) app the webview must
 * never navigate to it — doing so would replace the whole app with e.g. the Stripe portal — so we
 * hand the URL to the OS via the opener plugin. In the browser build it's a normal new tab.
 */
export function openExternal(url: string): void {
  if (inTauri()) {
    void import('@tauri-apps/plugin-opener')
      .then(({ openUrl }) => openUrl(url))
      .catch(() => window.open(url, '_blank', 'noopener,noreferrer'))
  } else {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

/**
 * Tag a Stripe checkout link so a DESKTOP purchase is relayed back to the app. The marker rides
 * Stripe's client_reference_id → the Checkout Session → /api/activate, which then bounces the
 * entitlement token to the app (so a new desktop subscriber never has to also claim by email).
 */
export function checkoutUrl(base: string): string {
  if (!inTauri()) return base
  try {
    const u = new URL(base)
    u.searchParams.set('client_reference_id', 'desktop')
    return u.toString()
  } catch {
    return base
  }
}

/** The single source of truth for the deep link that hands an entitlement token to the desktop app. */
export function desktopTokenLink(token: string): string {
  return `syqel-art://claim?token=${encodeURIComponent(token)}`
}

/**
 * Toggle fullscreen. In the desktop app the webview is a WKWebView/WebView2 that doesn't honor the
 * HTML5 Fullscreen API, so we drive the native window via Tauri; on the web it's the browser API
 * (which also hides the tab-capture "sharing" bar while active).
 */
export function toggleFullscreen(): void {
  if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) {
    void (async () => {
      const { getCurrentWindow } = await import('@tauri-apps/api/window')
      const win = getCurrentWindow()
      const full = await win.isFullscreen()
      await win.setFullscreen(!full)
      if (!full) track('fullscreen', { surface: surface() })
    })().catch(() => undefined)
    return
  }
  if (document.fullscreenElement) {
    void document.exitFullscreen()
  } else {
    void document.documentElement.requestFullscreen()
    track('fullscreen', { surface: surface() })
  }
}

/** True inside the desktop (Tauri) app — entitlement there is bearer-token, not a same-origin cookie. */
function inTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
}

/**
 * Distribution flavor of the desktop build: `standalone` (direct download — Stripe + self-updater),
 * `appstore` (Mac App Store — StoreKit IAP, no self-updater), or `msstore` (Microsoft Store — same
 * Stripe commerce as standalone, but no self-updater; the Store delivers updates). The web build is
 * always 'standalone'. Resolved once from the Rust `app_flavor` command and cached.
 */
export type AppFlavor = 'standalone' | 'appstore' | 'msstore'
let cachedFlavor: AppFlavor | null = null
/** DEV-only: `?paywall=appstore` forces the App Store paywall preview (for store screenshots). */
export function paywallPreview(): boolean {
  return import.meta.env.DEV
    && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('paywall') === 'appstore'
}
export async function resolveFlavor(): Promise<AppFlavor> {
  if (cachedFlavor) return cachedFlavor
  if (paywallPreview()) {
    cachedFlavor = 'appstore'
    return cachedFlavor
  }
  if (!inTauri()) {
    cachedFlavor = 'standalone'
    return cachedFlavor
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    cachedFlavor = await invoke<AppFlavor>('app_flavor')
  } catch {
    cachedFlavor = 'standalone'
  }
  return cachedFlavor
}
/** Synchronous flavor check — defaults to false until resolveFlavor() has run (call it at startup). */
export function isAppStore(): boolean {
  return cachedFlavor === 'appstore'
}

/**
 * Entitlement API origin. On the web it's same-origin (relative). In the desktop app the webview
 * is served from a bundled/local origin with no Worker, so we hit syqel.art absolutely and prove
 * entitlement with the stored bearer token instead of the (unavailable) HttpOnly cookie.
 */
const ENT_ORIGIN = 'https://syqel.art'
export function entApiBase(): string {
  return inTauri() ? ENT_ORIGIN : ''
}

const TOKEN_KEY = 'syqel.entToken'
/** The desktop entitlement token (minted by /api/claim or /api/activate, delivered via deep link). */
export function entToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}
export function setEntToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch { /* private mode */ }
}

// Runtime entitlement set out-of-band (no cookie): a subscribed SENDER announces it over the cast
// control channel (phone Play Billing / desktop Stripe → the TV drops the watermark for the session),
// and the native Android shell injects `window.__syqelEntitled` for its own attract-mode visuals.
let runtimeEntitled = false

// THE OFFER SIGNAL — what this player may offer changed: entitlement (below), the extended fx base going
// reachable / unreachable, or the weak-device tier (above). Every list in this file derives from those,
// so the grids, pickers and Director pools re-derive on this one signal.
//
// Entitlement arrives LATE and can be revoked — on the TV a sender announces it or leaves; on the web /
// desktop /api/status (or StoreKit) answers after the player has mounted, a claim deep link lands
// mid-session, a cancelled subscription verifies false. The grid's locks and the Director's pool are
// derived from it, so they need a change signal rather than a one-shot read. EVERY source feeds it:
// the cast control channel (setRuntimeEntitled), the native shells' global (the accessor below) and
// the verified cache (setVerifiedEntitlement, from refreshEntitlement).
const offerListeners = new Set<() => void>()
/** Subscribe to offer changes — entitlement, extended-fx reachability, the weak-device tier (relock /
 *  unlock / re-list the TV grid + re-gate the Directors' pools). Returns an unsubscribe function. */
export function onOfferChange(cb: () => void): () => void {
  offerListeners.add(cb)
  return () => { offerListeners.delete(cb) }
}
function notifyOfferChange(): void {
  offerRev += 1 // every memoized list is stale now (memo)
  for (const cb of [...offerListeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
}
onFxReachabilityChange(notifyOfferChange)
onHeavyChange(notifyOfferChange)

/**
 * The native phone shells (iOS SyqelArtApp, Android MainActivity) grant entitlement by ASSIGNING
 * `window.__syqelEntitled = true` once billing resolves — which happens well after this page mounts,
 * and is a plain assignment with no signal attached. The phone visualizer renders the same TvReceiver
 * as the TV, so without a notification a paying customer would sit on the free-tier Director pool for
 * the whole session. Back the global with an accessor so the shells' existing (already-shipped) code
 * transparently drives onOfferChange — no shell change required, old shells included.
 */
function installEntitlementGlobalHook(): void {
  const g = globalThis as { __syqelEntitled?: boolean }
  try {
    let injected = g.__syqelEntitled === true // the shell may have assigned before this module ran
    Object.defineProperty(g, '__syqelEntitled', {
      configurable: true,
      get: () => injected,
      set: (v: unknown) => {
        const on = v === true
        if (on === injected) return
        injected = on
        notifyOfferChange()
      }
    })
  } catch { /* frozen/exotic global — isSubscribed() still reads the plain value */ }
}
installEntitlementGlobalHook()

/** Called by the cast receiver when a sender announces (or revokes) entitlement over the control channel. */
export function setRuntimeEntitled(on: boolean): void {
  if (runtimeEntitled === on) return // only a real transition should relock the grid / rebuild the Director
  runtimeEntitled = on
  notifyOfferChange()
}

/** Desktop SENDER: tell the native cast server our entitlement so it announces it to TV receivers
 *  (a subscribed desktop's cast is watermark-free on the TV — the same control frame the phone sends). */
function pushCastEntitlement(on: boolean): void {
  if (!inTauri()) return
  void import('@tauri-apps/api/core')
    .then(({ invoke }) => invoke('audio_cast_set_entitled', { entitled: on }))
    .catch(() => { /* command absent (older shell) — ignore */ })
}

/**
 * Store a VERIFIED answer (the /api/status Worker or StoreKit) as the cached entitlement, and signal
 * when that flips what isSubscribed() says. The cache used to be written silently, so the web/desktop
 * player — which verifies AFTER it has built its Director — never learnt that a Pro user had arrived
 * (or that a subscription had lapsed) until a reload.
 */
function setVerifiedEntitlement(ok: boolean): void {
  const before = isSubscribed()
  try { localStorage.setItem('syqel.sub', ok ? '1' : '0') } catch { /* private mode */ }
  if (isSubscribed() !== before) notifyOfferChange()
}

/** Cached entitlement (kept fresh by refreshEntitlement — the signed-cookie/bearer /api/status check). */
export function isSubscribed(): boolean {
  if (runtimeEntitled) return true
  try {
    if ((globalThis as { __syqelEntitled?: boolean }).__syqelEntitled === true) return true
  } catch { /* no globalThis */ }
  try {
    return localStorage.getItem('syqel.sub') === '1'
  } catch {
    return false
  }
}

/**
 * syqel.art's own checkout (the pay.syqel.com links → /api/activate cookie or desktop token), asked of
 * the entitlement Worker. Web: same-origin signed cookie. Desktop: absolute syqel.art call with
 * `Authorization: Bearer <token>` (rolling the token forward if the Worker re-issues one). null = the
 * Worker couldn't answer (offline, or the vite dev server has no functions).
 */
async function artCheckoutPaid(tauri: boolean): Promise<boolean | null> {
  const token = tauri ? entToken() : null
  if (tauri && !token) return false // never checked out here: no token to validate
  try {
    const res = await fetch(`${entApiBase()}/api/status`, {
      credentials: tauri ? 'omit' : 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
    if (!res.ok) return null
    const data = (await res.json()) as { subscribed?: boolean; token?: string }
    const ok = data.subscribed === true
    if (tauri && data.token) setEntToken(data.token) // rolling renewal past the revalidation window
    if (tauri && !ok) setEntToken(null) // cancelled/invalid — drop the dead token
    return ok
  } catch {
    return null
  }
}

/**
 * THE entitlement check for the web and desktop players: Art is paid when any verified source says so —
 * syqel.art's own checkout, or the signed-in SYQEL account's `apps.art` (a plan bought on syqel.com,
 * or a legacy Pro / Deluxe / Elite / lifetime the platform folds in). The Mac App Store build answers
 * from StoreKit alone (Apple's rules). The answer is cached; when no source could answer, the cache stands.
 */
export async function refreshEntitlement(): Promise<boolean> {
  // Mac App Store build: entitlement is the local StoreKit subscription (Apple-account-scoped),
  // not the syqel.art Worker. Ask the StoreKit bridge instead.
  if ((await resolveFlavor()) === 'appstore') {
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      const ok = await invoke<boolean>('iap_entitlement')
      setVerifiedEntitlement(ok)
      pushCastEntitlement(ok)
      return ok
    } catch {
      return isSubscribed()
    }
  }
  const tauri = inTauri()
  const [checkout, account] = await Promise.all([
    artCheckoutPaid(tauri),
    import('./syqelAccount').then((m) => m.accountArtPaid(tauri)).catch(() => false)
  ])
  const ok = combineEntitlement(checkout, account)
  if (ok === null) return isSubscribed()
  setVerifiedEntitlement(ok)
  pushCastEntitlement(ok)
  return ok
}
